import { supabase } from '../config/supabase';
import { formatCurrency } from '../utils/currency';
import { inventoryService } from './inventoryService';
import { contactsService } from './contactsService';
import { Contact } from './contactsService';

// Utility functions for atomic operations
const atomicUtils = {
  // Check if error is network-related
  isNetworkError: (error: any): boolean => {
    return error?.message?.includes('fetch') ||
           error?.message?.includes('network') ||
           error?.code === 'NETWORK_ERROR' ||
           !navigator.onLine;
  },

  // Retry function with exponential backoff
  retryWithBackoff: async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> => {
    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        if (attempt === maxRetries || !atomicUtils.isNetworkError(error)) {
          throw error;
        }

        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  },

  // Enhanced error logging
  logTransactionError: (operation: string, error: Error, transactionLog: string[]) => {
    const errorDetails = {
      operation,
      message: error.message,
      timestamp: new Date().toISOString(),
      isNetworkError: atomicUtils.isNetworkError(error),
      transactionLog: [...transactionLog]
    };

    console.error('Atomic transaction failed:', errorDetails);
  },

  // Transaction logger for debugging
  createTransactionLogger: (operation: string) => {
    const logs: string[] = [];
    const startTime = Date.now();

    return {
      log: (message: string) => {
        const timestamp = new Date().toISOString();
        const logEntry = `[${timestamp}] ${message}`;
        logs.push(logEntry);
        console.log(`${operation}:`, message);
      },

      getLogs: () => [...logs],

      getDuration: () => Date.now() - startTime,

      logError: (error: Error) => {
        atomicUtils.logTransactionError(operation, error, logs);
      }
    };
  }
};

export interface RepairOrder {
  id: string;
  business_id: string;
  repair_order_number: string;
  customer_id: string;
  customer?: Contact;

  // Device Details (separate fields)
  device_brand: string;
  device_model: string;
  device_serial: string;
  device_problem: string;
  device_diagnosis?: string;

  // Status and Dates
  status: 'pending' | 'process' | 'completed' | 'invoiced' | 'cancelled';
  entry_date: string;
  completed_date?: string;

  // Technician assignment
  technician_id?: string;
  technician?: any;

  // Metadata
  created_at: string;
  updated_at: string;
}

export interface RepairOrderItem {
  id: string;
  repair_id: string;
  item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  created_at: string;
}

export interface RepairOrderFormData {
  repair_order_number?: string;
  customer_id: string;

  // Device Details
  device_brand: string;
  device_model: string;
  device_serial: string;
  device_problem: string;
  device_diagnosis?: string;

  // Status and Dates
  status: 'pending' | 'process' | 'completed' | 'invoiced' | 'cancelled';
  entry_date: string;
  completed_date?: string;

  // Technician
  technician_id?: string;

  // Items
  items: RepairOrderItemFormData[];
}

export interface RepairOrderItemFormData {
  item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  item_type: 'inventory' | 'non_inventory';
  sku?: string; // Only for inventory items
  available_stock?: number; // Available stock for inventory items
}

// Type for repair order with items
export interface RepairOrderWithItems extends RepairOrder {
  items: (RepairOrderItem & { item_name: string })[];
}

// Stock validation result
export interface StockValidationResult {
  item_id: string;
  item_name: string;
  requested_quantity: number;
  available_quantity: number;
  status: 'available' | 'insufficient';
}

export const repairOrdersService = {
  // Generate repair order number (RO-XXXXXX format)
  async generateRepairOrderNumber(): Promise<string> {
    try {
      // Call the database function to generate the number
      const { data, error } = await supabase.rpc('generate_repair_order_number');

      if (error) throw error;
      return data || 'RO-000001'; // Fallback if function fails
    } catch (error) {
      console.error('Error generating repair order number:', error);
      // Fallback: generate based on timestamp
      const timestamp = Date.now().toString().slice(-6);
      return `RO-${timestamp.padStart(6, '0')}`;
    }
  },

  // Get all repair orders for the business
  async getRepairOrders(businessId?: string): Promise<{ orders: RepairOrder[]; error: Error | null }> {
    try {
      let query = supabase
        .from('repair_orders')
        .select(`
          *,
          customer:contacts (*),
          technician:users (*)
        `)
        .order('created_at', { ascending: false });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { orders: data || [], error: null };
    } catch (error) {
      return { orders: [], error: error as Error };
    }
  },

  // Get repair order by ID with items (public access - no business restriction)
  async getRepairOrderByIdPublic(id: string): Promise<{ order: RepairOrderWithItems | null; error: Error | null }> {
    try {
      console.log(`Fetching repair order by ID (public): ${id}`);

      const { data: orderData, error: orderError } = await supabase
        .from('repair_orders')
        .select(`
          *,
          customer:contacts (*),
          technician:users (*)
        `)
        .eq('id', id)
        .single();

      if (orderError) {
        console.error('Error fetching repair order (public):', orderError);
        throw orderError;
      }
      if (!orderData) {
        console.log('Repair order not found (public)');
        throw new Error('Repair order not found');
      }

      console.log('Repair order data (public):', orderData);

      // Get the items for this repair order
      console.log('Fetching items for repair order (public):', id);
      const { data: itemsData, error: itemsError } = await supabase
        .from('repair_order_items')
        .select(`
          id,
          repair_id,
          item_id,
          quantity,
          unit_price,
          total_price,
          created_at,
          item:inventory_items (name)
        `)
        .eq('repair_id', id);

      if (itemsError) {
        console.error('Error fetching repair order items (public):', itemsError);
        throw itemsError;
      }

      console.log('Raw items data (public):', itemsData);

      // Combine order and items
      const orderWithItems = {
        ...orderData,
        items: itemsData?.map(item => ({
          id: item.id,
          repair_id: item.repair_id,
          item_id: item.item_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          created_at: item.created_at,
          item_name: (item.item as any)?.name || ''
        })) || []
      };

      console.log('Final order with items (public):', orderWithItems);
      return { order: orderWithItems, error: null };
    } catch (error) {
      console.error('Error in getRepairOrderByIdPublic:', error);
      return { order: null, error: error as Error };
    }
  },

  // Get repair order by ID with items
  async getRepairOrderById(id: string, businessId?: string): Promise<{ order: RepairOrderWithItems | null; error: Error | null }> {
    try {
      console.log(`Fetching repair order by ID: ${id}, businessId: ${businessId}`);

      let query = supabase
        .from('repair_orders')
        .select(`
          *,
          customer:contacts (*),
          technician:users (*)
        `)
        .eq('id', id);

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data: orderData, error: orderError } = await query.single();

      if (orderError) {
        console.error('Error fetching repair order:', orderError);
        throw orderError;
      }
      if (!orderData) {
        console.log('Repair order not found');
        throw new Error('Repair order not found');
      }

      console.log('Repair order data:', orderData);

      // Get the items for this repair order
      console.log('Fetching items for repair order:', id);
      const { data: itemsData, error: itemsError } = await supabase
        .from('repair_order_items')
        .select(`
          id,
          repair_id,
          item_id,
          quantity,
          unit_price,
          total_price,
          created_at,
          item:inventory_items (name)
        `)
        .eq('repair_id', id);

      if (itemsError) {
        console.error('Error fetching repair order items:', itemsError);
        throw itemsError;
      }

      console.log('Raw items data:', itemsData);

      // Combine order and items
      const orderWithItems = {
        ...orderData,
        items: itemsData?.map(item => ({
          id: item.id,
          repair_id: item.repair_id,
          item_id: item.item_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          created_at: item.created_at,
          item_name: (item.item as any)?.name || ''
        })) || []
      };

      console.log('Final order with items:', orderWithItems);
      return { order: orderWithItems, error: null };
    } catch (error) {
      console.error('Error in getRepairOrderById:', error);
      return { order: null, error: error as Error };
    }
  },

  // Validate stock before creating/updating repair order
  async validateStock(items: RepairOrderItemFormData[], existingOrderId?: string): Promise<{ results: StockValidationResult[]; error: Error | null }> {
    try {
      const results: StockValidationResult[] = [];

      // If editing existing order, get current allocation to adjust available stock
      let existingAllocations: { [itemId: string]: number } = {};
      if (existingOrderId) {
        const { order, error: fetchError } = await this.getRepairOrderById(existingOrderId);
        if (fetchError) throw fetchError;

        if (order) {
          // Build map of existing allocations for this repair order
          for (const existingItem of order.items) {
            if (existingItem.item_id) { // Only track inventory items (non-null item_id)
              existingAllocations[existingItem.item_id] = (existingAllocations[existingItem.item_id] || 0) + existingItem.quantity;
            }
          }
        }
      }

      for (const item of items) {
        // Skip stock validation for non-inventory items
        if (item.item_type === 'non_inventory') {
          results.push({
            item_id: item.item_id,
            item_name: item.item_name,
            requested_quantity: item.quantity,
            available_quantity: item.quantity, // Non-inventory items are always "available"
            status: 'available'
          });
          continue;
        }

        // Validate stock for inventory items
        const { item: inventoryItem, error } = await inventoryService.getInventoryItemById(item.item_id);

        if (error) throw error;
        if (!inventoryItem) {
          results.push({
            item_id: item.item_id,
            item_name: item.item_name,
            requested_quantity: item.quantity,
            available_quantity: 0,
            status: 'insufficient'
          });
          continue;
        }

        // Calculate effective available stock by adding back what's already allocated to this repair order
        const alreadyAllocated = existingAllocations[item.item_id] || 0;
        const effectiveAvailableStock = inventoryItem.quantity + alreadyAllocated;

        results.push({
          item_id: item.item_id,
          item_name: inventoryItem.name,
          requested_quantity: item.quantity,
          available_quantity: effectiveAvailableStock,
          status: effectiveAvailableStock >= item.quantity ? 'available' : 'insufficient'
        });
      }

      return { results, error: null };
    } catch (error) {
      return { results: [], error: error as Error };
    }
  },

  // Create a new repair order with atomic operations
  async createRepairOrderAtomic(orderData: RepairOrderFormData, businessId?: string): Promise<{ order: RepairOrder | null; error: Error | null }> {
    const logger = atomicUtils.createTransactionLogger('createRepairOrder');
    const rollbackActions: (() => Promise<void>)[] = [];

    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      // Use provided repair order number or generate one
      const repairOrderNumber = orderData.repair_order_number || await this.generateRepairOrderNumber();
      logger.log(`Starting atomic repair order creation for ${repairOrderNumber}`);

      // Validate stock before proceeding
      logger.log('Step 1: Validating stock availability');
      const { results: stockValidation, error: stockError } = await this.validateStock(orderData.items);

      if (stockError) throw stockError;

      const insufficientItems = stockValidation.filter(item => item.status === 'insufficient');
      if (insufficientItems.length > 0) {
        const itemNames = insufficientItems.map(item => item.item_name).join(', ');
        throw new Error(`Insufficient stock for items: ${itemNames}. Please adjust quantities or check inventory.`);
      }

      // Validate completed_date if status is completed
      if (orderData.status === 'completed' && !orderData.completed_date) {
        throw new Error('Completed date is required when status is Completed');
      }

      // Step 2: Create the repair order record
      logger.log('Step 2: Creating repair order record');
      const { data: order, error: orderError } = await atomicUtils.retryWithBackoff(async () => {
        const result = await supabase
          .from('repair_orders')
          .insert([
            {
              business_id: businessId,
              repair_order_number: repairOrderNumber,
              customer_id: orderData.customer_id,
              device_brand: orderData.device_brand,
              device_model: orderData.device_model,
              device_serial: orderData.device_serial,
              device_problem: orderData.device_problem,
              device_diagnosis: orderData.device_diagnosis,
              status: orderData.status,
              entry_date: orderData.entry_date,
              completed_date: orderData.completed_date,
              technician_id: orderData.technician_id,
            },
          ])
          .select()
          .single();

        if (result.error) throw result.error;
        if (!result.data) throw new Error('Failed to create repair order - no data returned');
        return result;
      });

      if (orderError) throw orderError;
      if (!order) throw new Error('Failed to create repair order - no data returned');

      // Add rollback action for repair order
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Deleting repair order record');
        await supabase.from('repair_orders').delete().eq('id', order.id);
      });

      // Step 3: Create the repair order items and reduce inventory
      logger.log('Step 3: Creating repair order items and reducing inventory');
      if (orderData.items.length > 0) {
        const itemsToInsert = orderData.items.map(item => ({
          repair_id: order.id,
          item_id: item.item_type === 'inventory' ? item.item_id : null,
          item_name: item.item_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
        }));

        const { error: itemsError } = await supabase
          .from('repair_order_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;

        // Add rollback action for items
        rollbackActions.push(async () => {
          logger.log('ROLLBACK: Deleting repair order items');
          await supabase.from('repair_order_items').delete().eq('repair_id', order.id);
        });

        // Step 4: Reduce inventory stock and create transactions for each item
        logger.log('Step 4: Reducing inventory stock and creating transactions');
        for (const item of orderData.items) {
          if (item.item_type === 'inventory') {
            // Reduce inventory stock for inventory items
            const { item: updatedItem, error: inventoryError } = await inventoryService.updateInventoryFromUsage(
              item.item_id,
              item.quantity, // Positive quantity for usage (method handles the reduction)
              'repair_order',
              order.id
            );

            if (inventoryError) throw inventoryError;

            // Add rollback action for inventory
            rollbackActions.push(async () => {
              logger.log(`ROLLBACK: Restoring inventory for item ${item.item_id}`);
              await inventoryService.updateInventoryFromUsage(
                item.item_id,
                -item.quantity, // Negative quantity to restore stock
                'repair_order',
                order.id
              );
            });
          }

          // Create inventory transaction for both inventory and non-inventory items
          try {
            const { error: transactionError } = await supabase.rpc('create_repair_inventory_transaction', {
              p_repair_id: order.id,
              p_item_id: item.item_type === 'inventory' ? item.item_id : null,
              p_item_name: item.item_name,
              p_quantity: item.quantity,
              p_unit_cost: item.unit_price,
              p_item_type: item.item_type
            });

            if (transactionError) {
              console.warn('Failed to create inventory transaction:', transactionError);
              // Don't throw here as the repair order was created successfully
            }
          } catch (transactionErr) {
            console.warn('Error creating inventory transaction:', transactionErr);
          }
        }
      }

      logger.log('Transaction completed successfully');
      return { order, error: null };

    } catch (error) {
      const err = error as Error;
      logger.log(`Transaction failed: ${err.message}`);

      // Log detailed error information
      logger.logError(err);

      // Execute rollback actions in reverse order
      logger.log('Starting rollback process...');
      for (let i = rollbackActions.length - 1; i >= 0; i--) {
        try {
          await rollbackActions[i]();
        } catch (rollbackError) {
          const rbErr = rollbackError as Error;
          logger.log(`Rollback step ${i} failed: ${rbErr.message}`);
          console.warn(`Rollback step ${i} failed:`, rbErr);
        }
      }

      logger.log('Rollback completed');

      // Return user-friendly error message
      const isNetworkError = atomicUtils.isNetworkError(err);
      const userMessage = isNetworkError
        ? 'Network connection lost. The operation has been rolled back. Please check your connection and try again.'
        : err.message.includes('Insufficient stock')
        ? err.message
        : 'Failed to create repair order. All changes have been rolled back. Please try again.';

      return { order: null, error: new Error(userMessage) };
    }
  },

  // Create a new repair order (legacy method - now uses atomic version)
  async createRepairOrder(orderData: RepairOrderFormData, businessId?: string): Promise<{ order: RepairOrder | null; error: Error | null }> {
    return this.createRepairOrderAtomic(orderData, businessId);
  },

  // Update an existing repair order
  async updateRepairOrder(id: string, orderData: RepairOrderFormData, businessId?: string): Promise<{ order: RepairOrder | null; error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      // Validate completed_date if status is completed
      if (orderData.status === 'completed' && !orderData.completed_date) {
        throw new Error('Completed date is required when status is Completed');
      }

      // Validate stock with existing order context
      const { results: stockValidation, error: stockError } = await this.validateStock(orderData.items, id);

      if (stockError) throw stockError;

      const insufficientItems = stockValidation.filter(item => item.status === 'insufficient');
      if (insufficientItems.length > 0) {
        const itemNames = insufficientItems.map(item => item.item_name).join(', ');
        throw new Error(`Insufficient stock for items: ${itemNames}. Please adjust quantities or check inventory.`);
      }

      const updateData: any = {
        repair_order_number: orderData.repair_order_number,
        customer_id: orderData.customer_id,
        device_brand: orderData.device_brand,
        device_model: orderData.device_model,
        device_serial: orderData.device_serial,
        device_problem: orderData.device_problem,
        device_diagnosis: orderData.device_diagnosis,
        status: orderData.status,
        entry_date: orderData.entry_date,
        completed_date: orderData.completed_date,
        technician_id: orderData.technician_id,
      };

      const { data: order, error: orderError } = await supabase
        .from('repair_orders')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (orderError) throw orderError;

      // Get existing items to calculate stock adjustments
      const { order: existingOrder, error: fetchError } = await this.getRepairOrderById(id, businessId);
      if (fetchError) throw fetchError;

      // Calculate stock adjustments based on differences
      const existingItemsMap = new Map();
      if (existingOrder) {
        existingOrder.items.forEach(item => {
          existingItemsMap.set(item.item_id, item.quantity);
        });
      }

      // Process stock adjustments for inventory items
      for (const newItem of orderData.items) {
        if (newItem.item_type === 'inventory') {
          const existingQuantity = existingItemsMap.get(newItem.item_id) || 0;
          const quantityDifference = newItem.quantity - existingQuantity;

          if (quantityDifference > 0) {
            // Need more stock - reduce inventory
            const { item: updatedItem, error: inventoryError } = await inventoryService.updateInventoryFromUsage(
              newItem.item_id,
              quantityDifference, // Positive for reduction
              'repair_order',
              id
            );
            if (inventoryError) throw inventoryError;
          } else if (quantityDifference < 0) {
            // Have excess stock - restore to inventory
            const { item: updatedItem, error: inventoryError } = await inventoryService.updateInventoryFromUsage(
              newItem.item_id,
              quantityDifference, // Negative for restoration
              'repair_order',
              id
            );
            if (inventoryError) throw inventoryError;
          }
          // If quantityDifference === 0, no stock adjustment needed
        }
      }

      // Handle items that were completely removed (restore full quantity to inventory)
      existingItemsMap.forEach(async (existingQuantity, itemId) => {
        const stillExists = orderData.items.some(newItem => newItem.item_id === itemId);
        if (!stillExists && existingQuantity > 0 && itemId) { // Only restore inventory items (non-null item_id)
          // This item was removed - restore its quantity to inventory
          const { item: updatedItem, error: inventoryError } = await inventoryService.updateInventoryFromUsage(
            itemId,
            -existingQuantity, // Negative to restore
            'repair_order',
            id
          );
          if (inventoryError) throw inventoryError;
        }
      });

      // Delete existing items
      const { error: deleteError } = await supabase
        .from('repair_order_items')
        .delete()
        .eq('repair_id', id);

      if (deleteError) throw deleteError;

      // Create the new repair order items
      if (orderData.items.length > 0) {
        const itemsToInsert = orderData.items.map(item => ({
          repair_id: id,
          item_id: item.item_type === 'inventory' ? item.item_id : null,
          item_name: item.item_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
        }));

        const { error: itemsError } = await supabase
          .from('repair_order_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      return { order, error: null };
    } catch (error) {
      return { order: null, error: error as Error };
    }
  },

  // Delete a repair order with atomic operations
  async deleteRepairOrderAtomic(id: string, businessId?: string): Promise<{ error: Error | null }> {
    const logger = atomicUtils.createTransactionLogger('deleteRepairOrder');
    const rollbackActions: (() => Promise<void>)[] = [];

    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      logger.log(`Starting atomic repair order deletion for ID: ${id}`);

      // Step 1: Get the repair order data before deletion
      logger.log('Step 1: Fetching repair order data');
      const { order, error: fetchError } = await this.getRepairOrderById(id, businessId);

      if (fetchError) throw fetchError;
      if (!order) throw new Error('Repair order not found');

      // Check if order can be deleted (only pending or cancelled)
      if (order.status !== 'pending' && order.status !== 'cancelled') {
        throw new Error('Only pending or cancelled repair orders can be deleted');
      }

      // Step 2: Restore inventory stock for each item
      logger.log('Step 2: Restoring inventory stock');
      for (const item of order.items) {
        // Only restore stock for inventory items (non-null item_id)
        if (item.item_id) {
          const { item: updatedItem, error: inventoryError } = await inventoryService.updateInventoryFromUsage(
            item.item_id,
            -item.quantity, // Negative quantity to restore stock (method handles the addition)
            'repair_order',
            id
          );

          if (inventoryError) throw inventoryError;

          // Add rollback action for inventory
          rollbackActions.push(async () => {
            logger.log(`ROLLBACK: Reducing inventory for item ${item.item_id}`);
            await inventoryService.updateInventoryFromUsage(
              item.item_id,
              item.quantity, // Positive quantity to reduce again
              'repair_order',
              id
            );
          });
        }
      }

      // Step 3: Delete repair order items
      logger.log('Step 3: Deleting repair order items');
      const { error: itemsError } = await supabase
        .from('repair_order_items')
        .delete()
        .eq('repair_id', id);

      if (itemsError) throw itemsError;

      // Add rollback action for items
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Restoring repair order items');
        const itemsToRestore = order.items.map(item => ({
          repair_id: id,
          item_id: item.item_id || null, // Handle null item_id for non-inventory items
          item_name: item.item_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
        }));
        await supabase.from('repair_order_items').insert(itemsToRestore);
      });

      // Step 4: Delete the repair order
      logger.log('Step 4: Deleting repair order record');
      const { error: deleteError } = await supabase
        .from('repair_orders')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Add rollback action for repair order
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Restoring repair order record');
        await supabase.from('repair_orders').insert([{
          id: order.id,
          business_id: order.business_id,
          repair_order_number: order.repair_order_number,
          customer_id: order.customer_id,
          device_brand: order.device_brand,
          device_model: order.device_model,
          device_serial: order.device_serial,
          device_problem: order.device_problem,
          device_diagnosis: order.device_diagnosis,
          status: order.status,
          entry_date: order.entry_date,
          completed_date: order.completed_date,
          technician_id: order.technician_id,
        }]);
      });

      logger.log('Transaction completed successfully');
      return { error: null };

    } catch (error) {
      const err = error as Error;
      logger.log(`Transaction failed: ${err.message}`);

      // Log detailed error information
      logger.logError(err);

      // Execute rollback actions in reverse order
      logger.log('Starting rollback process...');
      for (let i = rollbackActions.length - 1; i >= 0; i--) {
        try {
          await rollbackActions[i]();
        } catch (rollbackError) {
          const rbErr = rollbackError as Error;
          logger.log(`Rollback step ${i} failed: ${rbErr.message}`);
          console.warn(`Rollback step ${i} failed:`, rbErr);
        }
      }

      logger.log('Rollback completed');

      // Return user-friendly error message
      const isNetworkError = atomicUtils.isNetworkError(err);
      const userMessage = isNetworkError
        ? 'Network connection lost. The operation has been rolled back. Please check your connection and try again.'
        : err.message;

      return { error: new Error(userMessage) };
    }
  },

  // Delete a repair order (legacy method - now uses atomic version)
  async deleteRepairOrder(id: string, businessId?: string): Promise<{ error: Error | null }> {
    return this.deleteRepairOrderAtomic(id, businessId);
  },

  // Get repair order formatted total
  getFormattedTotal(amount: number): string {
    return formatCurrency(amount);
  },

  // Get repair order status label
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending: 'Pending',
      process: 'In Process',
      completed: 'Completed',
      invoiced: 'Invoiced',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  },

  // Get repair order status badge class
  getStatusBadgeClass(status: string): string {
    const classes: Record<string, string> = {
      pending: 'badge-warning',
      process: 'badge-info',
      completed: 'badge-success',
      invoiced: 'badge-success',
      cancelled: 'badge-error',
    };
    return classes[status] || 'badge';
  },

  // Update repair order status
  async updateRepairOrderStatus(
    id: string,
    status: 'pending' | 'process' | 'completed' | 'invoiced' | 'cancelled',
    businessId?: string
  ): Promise<{ order: RepairOrder | null; error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      // If status is completed, set completed_date if not already set
      let updateData: any = { status };
      if (status === 'completed') {
        updateData.completed_date = new Date().toISOString().split('T')[0];
      }

      const { data: order, error } = await supabase
        .from('repair_orders')
        .update(updateData)
        .eq('id', id)
        .eq('business_id', businessId)
        .select()
        .single();

      if (error) throw error;
      return { order, error: null };
    } catch (error) {
      return { order: null, error: error as Error };
    }
  },
};